import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Copy, History, Save, Trash2 } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { AiAssistantWidget } from '@/components/AiAssistantWidget'
import { useAuthStore } from '@/stores/auth'
import {
  PROMPT_BANNED_PATTERNS_DEFAULT,
  findBannedPattern,
  type AiAssistantPageContext,
  type Campaign,
  type CampaignListResponse,
  type Prompt,
  type PromptResponse,
} from '@sdr-agend/admin-schemas'
import { usePromptConfig } from '@/hooks/usePromptConfig'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

/**
 * `/admin/prompts/:id` — current version detail + edit form.
 *
 * Story 3.7 §AC #7 + §AC #10: card with current version, char counter, banned
 * pattern warning, save (creates new version) + activate toggle.
 */
export function PromptDetailPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'admin'
  const canMutate = role === 'admin' || role === 'manager'

  const [systemPrompt, setSystemPrompt] = useState('')
  const [rationale, setRationale] = useState('Ajuste de tom')
  const [activate, setActivate] = useState(true)
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })
  // Duplicar-para-campanha: alterna o painel + guarda a campanha destino.
  const [duplicating, setDuplicating] = useState(false)
  const [targetCampaignId, setTargetCampaignId] = useState('')

  const promptPageContext: AiAssistantPageContext | null =
    id !== undefined && id !== '' ? { page: 'prompt', promptId: id } : null

  const { data, isLoading } = useQuery<PromptResponse>({
    queryKey: ['admin-prompts', id],
    queryFn: async () => api<PromptResponse>(`/admin/prompts/${id ?? ''}`),
    enabled: id !== undefined,
  })

  useEffect(() => {
    if (data?.prompt !== undefined) {
      setSystemPrompt(data.prompt.systemPrompt)
    }
  }, [data])

  const prompt: Prompt | undefined = data?.prompt

  function notify(state: Omit<ToastState, 'open'>): void {
    setToast({ open: true, ...state })
  }

  const updateMutation = useMutation({
    mutationFn: async () =>
      api<PromptResponse>(`/admin/prompts/${id ?? ''}`, {
        method: 'PATCH',
        body: JSON.stringify({ systemPrompt, rationale, activate }),
      }),
    onSuccess: () => {
      notify({
        variant: 'default',
        title: activate ? 'Nova versão ativada' : 'Nova versão criada',
        description: activate
          ? 'O agente passa a usar este prompt imediatamente.'
          : 'A versão fica em histórico até ser ativada.',
      })
      void queryClient.invalidateQueries({ queryKey: ['admin-prompts'] })
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao salvar', description: msg })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () =>
      api<PromptResponse>(`/admin/prompts/${id ?? ''}`, { method: 'DELETE' }),
    onSuccess: () => {
      notify({ variant: 'default', title: 'Prompt removido' })
      void queryClient.invalidateQueries({ queryKey: ['admin-prompts'] })
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao remover', description: msg })
    },
  })

  // Lista de campanhas para o seletor de destino (só busca quando o painel abre).
  const { data: campaignsData } = useQuery<CampaignListResponse>({
    queryKey: ['admin-campaigns', 'for-duplicate'],
    queryFn: async () => api<CampaignListResponse>('/admin/campaigns'),
    enabled: duplicating,
  })
  const targetCampaigns: Campaign[] = (campaignsData?.campaigns ?? []).filter(
    (c) => c.id !== prompt?.campaignId,
  )

  const duplicateMutation = useMutation({
    mutationFn: async () =>
      api<PromptResponse>(`/admin/prompts/${id ?? ''}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ campaignId: targetCampaignId }),
      }),
    onSuccess: (res) => {
      setDuplicating(false)
      setTargetCampaignId('')
      void queryClient.invalidateQueries({ queryKey: ['admin-prompts'] })
      notify({
        variant: 'default',
        title: 'Prompt duplicado',
        description: 'Criado e ativado na campanha destino. Abrindo o novo prompt…',
      })
      navigate(`/admin/prompts/${res.prompt.id}`)
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      notify({ variant: 'destructive', title: 'Falha ao duplicar', description: msg })
    },
  })

  // Live banned-pattern probe — uses the seed defaults for instant feedback.
  // Server-side validation is the source of truth (loads the live list).
  // `maxChars` mirrors `system_settings.prompt_max_chars` so admins can lift
  // the cap from `/settings` without a redeploy.
  const { maxChars, minChars } = usePromptConfig()
  const bannedHit = findBannedPattern(systemPrompt, PROMPT_BANNED_PATTERNS_DEFAULT)
  const tooShort = systemPrompt.length < minChars
  const tooLong = systemPrompt.length > maxChars
  const canSave =
    canMutate && !tooShort && !tooLong && bannedHit === null && rationale.trim() !== ''

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label="Voltar">
            <Link to="/admin/prompts">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {prompt?.campaignName ?? '...'}
            </h1>
            {prompt !== undefined && (
              <div className="mt-1 flex items-center gap-2">
                <Badge variant="secondary">Versão ativa: v{prompt.version}</Badge>
                <span className="text-xs text-muted-foreground">
                  Atualizado em {new Date(prompt.updatedAt).toLocaleString('pt-BR')}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to={`/admin/prompts/${id ?? ''}/history`}>
              <History className="h-4 w-4" aria-hidden="true" />
              Histórico
            </Link>
          </Button>
          {canMutate && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setDuplicating((v) => !v)}
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              Duplicar
            </Button>
          )}
          {isAdmin && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (window.confirm('Remover este prompt? Versões em histórico serão preservadas.')) {
                  deleteMutation.mutate()
                }
              }}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Remover
            </Button>
          )}
        </div>
      </div>

      {duplicating && (
        <div className="rounded-lg border bg-muted/30 p-4">
          <h2 className="text-sm font-medium">Duplicar prompt para outra campanha</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Cria um prompt novo (v1 ativa) na campanha escolhida com este mesmo
            conteúdo. Não altera o prompt atual. A campanha destino não pode já
            ter um prompt ativo.
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="grid gap-1">
              <Label htmlFor="dup-campaign">Campanha destino</Label>
              <select
                id="dup-campaign"
                value={targetCampaignId}
                onChange={(e) => setTargetCampaignId(e.target.value)}
                className="h-9 rounded-md border bg-background px-3 text-sm"
              >
                <option value="">Selecione…</option>
                {targetCampaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              onClick={() => duplicateMutation.mutate()}
              disabled={targetCampaignId === '' || duplicateMutation.isPending}
            >
              <Copy className="h-4 w-4" aria-hidden="true" />
              {duplicateMutation.isPending ? 'Duplicando…' : 'Confirmar duplicação'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setDuplicating(false)}>
              Cancelar
            </Button>
          </div>
          {targetCampaigns.length === 0 && (
            <p className="mt-2 text-xs text-amber-600">
              Não há outra campanha disponível como destino.
            </p>
          )}
        </div>
      )}

      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (canSave) updateMutation.mutate()
        }}
      >
        <div className="grid gap-2">
          <Label htmlFor="systemPrompt">System Prompt</Label>
          <Textarea
            id="systemPrompt"
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            rows={16}
            disabled={!canMutate || isLoading}
            aria-describedby="prompt-meta prompt-warning"
            placeholder="Você é um SDR experiente. Use o nome do cliente em {{customer_name}} para personalizar..."
          />
          <p id="prompt-meta" className="text-xs text-muted-foreground">
            {systemPrompt.length} / {maxChars} caracteres (mínimo {minChars})
            · prefira placeholders <code>{'{{customer_name}}'}</code>,{' '}
            <code>{'{{seller_name}}'}</code> em vez de PII real.
          </p>
          {bannedHit !== null && (
            <p
              id="prompt-warning"
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
            >
              Padrão proibido detectado: <strong>{bannedHit}</strong>. Remova antes de salvar.
            </p>
          )}
          {tooShort && (
            <p className="text-xs text-amber-600">
              Mínimo de {minChars} caracteres ainda não atingido.
            </p>
          )}
          {tooLong && (
            <p className="text-xs text-destructive">
              Excedeu o tamanho máximo de {maxChars} caracteres.
            </p>
          )}
        </div>

        <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end sm:gap-4">
          <div className="grid gap-2">
            <Label htmlFor="rationale">Motivo da alteração</Label>
            <Input
              id="rationale"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              maxLength={500}
              required
              disabled={!canMutate}
              placeholder="Ajuste de tom, correção de placeholders, etc."
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={activate}
              onChange={(e) => setActivate(e.target.checked)}
              disabled={!canMutate}
              className="h-4 w-4"
            />
            <span>Ativar imediatamente</span>
          </label>
        </div>

        {canMutate && (
          <div className="flex justify-end">
            <Button type="submit" disabled={!canSave || updateMutation.isPending}>
              <Save className="h-4 w-4" aria-hidden="true" />
              {updateMutation.isPending ? 'Salvando…' : 'Criar nova versão'}
            </Button>
          </div>
        )}
      </form>

      <Toast
        open={toast.open}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>

      <AiAssistantWidget key={id} pageContext={promptPageContext} />
    </div>
  )
}

function formatApiError(err: ApiError): string {
  if (err.bodyJson !== null && typeof err.bodyJson === 'object' && 'error' in err.bodyJson) {
    const e = (err.bodyJson as { error: { code?: string; message?: string } }).error
    return e.message ?? e.code ?? err.message
  }
  return err.message
}

export default PromptDetailPage
